/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  InAppNotificationAuthenticatePayloadMap,
  InAppNotificationCategory,
  InAppNotificationCategoryDataMap,
} from '@/lib/in-app-notifications/types'
import { useWebsocket } from '@/websocket/hook'
import { Logger } from '@/lib/logger'

const logger = new Logger('useInAppNotifications')

interface UseInAppNotificationsOptions<TCategory extends InAppNotificationCategory> {
  authenticationPayload?: InAppNotificationAuthenticatePayloadMap[TCategory]
  urlParams?: Record<string, string | number | boolean>
  enabled?: boolean
  onError?: (status: string, message: string) => void
}

const IN_APP_NOTIFICATION_URL = `${process.env.REACT_APP_WS_URL}/notifications/app`

export function useInAppNotifications<TCategory extends InAppNotificationCategory>(
  category: TCategory,
  options: UseInAppNotificationsOptions<TCategory> = {}
) {
  const [notification, setNotification] = useState<InAppNotificationCategoryDataMap[TCategory] | null>(null)

  const { authenticationPayload = {}, urlParams = {}, enabled = true } = options

  const params = useMemo(() => {
    const rawParams = new URLSearchParams()
    Object.entries(urlParams).forEach(([key, value]) => {
      rawParams.append(key, String(value))
    })
    return rawParams
  }, [urlParams])

  const { receive, send, authenticate, authenticated, ...websocket } = useWebsocket<
    InAppNotificationCategoryDataMap[TCategory]
  >({
    url: `${IN_APP_NOTIFICATION_URL}/${category}/${urlParams ? `?${params.toString()}` : ''}`,
    enabled,
    options: {
      auth: {
        payload: authenticationPayload as Record<string, unknown>,
        protected: true,
        autoConnect: true,
        onAuthenticationError: (error: string) => {
          logger.error('Falha de autenticação do socket:', error)
        },
      },
      onError: options.onError,
    },
  })

  const receivedMessage = receive()

  const clearLiveNotification = useCallback(() => {
    setNotification(null)
  }, [])

  useEffect(() => {
    setNotification(receivedMessage)
  }, [receivedMessage])

  return {
    notification,
    clearLiveNotification,
    ...websocket,
  }
}
